import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';

export type  MyComponent = {
    id : number;
    name : string;
}

export type MyProjectRole = {
    associateId : number;
    role : string;
}

export type MyProject = {
    id : number;
    name : string;
    isPmRole : boolean;
    pmFixedBilling : boolean;
    components : MyComponent[];
    roles : MyProjectRole[];
};

export type SchedulerProject = {
    id : number;
    name : string;
    type : string;
    teamId : number;
    jiraKey ?: string;
};

type Props = {
    children : ReactNode,
};

export type ProjectProvider = {
    myProjects : MyProject[];
    allProjects : SchedulerProject[];
    cachedProjects : MyProject[];
    getProject : (id : number) => Promise<MyProject | undefined>;
}

const MyProjectsContext = createContext<ProjectProvider>({
    myProjects: [],
    allProjects: [],
    cachedProjects: [],
    getProject: async () => undefined,
});

const MyProjectsProvider : React.FC<Props> = ({children} : Props) => {
    const apiFetch = useApiFetch();
    const [myProjects, setMyProjects] = useState<MyProject[]>([]);
    const [allProjects, setAllProjects] = useState<SchedulerProject[]>([]);
    const [cachedProjects, setCachedProjects] = useState<MyProject[]>([]);


    useEffect(() => {
        (async () => {
            const url = new URL('/v1/projects/my', apiEndpoint);
            const result = await apiFetch(url.href);

            if (!result.ok) {
                return;
            }

            const data = await result.json();
            setMyProjects(data);
        })();
    }, [setMyProjects, apiFetch]);

    useEffect(() => {
        (async () => {
            const url = new URL('/v1/scheduler/projects', apiEndpoint);
            const result = await apiFetch(url.href);

            if (!result.ok) {
                return;
            }

            const data = await result.json();
            setAllProjects(data);
        })();
    }, [setAllProjects, apiFetch]);

    const getProject = useCallback(async (id : number) => {
        const url = new URL(`/v1/projects/${id}`, apiEndpoint);
        const result = await apiFetch(url.href);

        if (!result.ok) {
            return undefined;
        }

        const data = await result.json() as MyProject;
        setCachedProjects([
            ...cachedProjects,
            data,
        ]);
        return data;
    },[apiFetch, cachedProjects]);

    return (
        <MyProjectsContext.Provider value={{
            myProjects,
            allProjects,
            cachedProjects,
            getProject,
        }}>
            {children}
        </MyProjectsContext.Provider>
    );
};

export const useProjects = () : ProjectProvider => {
    return useContext(MyProjectsContext);
};

export default MyProjectsProvider;
